import api from "./api";
import { resolveRoleName } from "../utils/auth";

// Company service (singular endpoints)
// Endpoints:
// - GET /company
// - GET /company/:id
// - POST /company
// - PATCH /company/:id
// - DELETE /company/:id

const normalizeCompany = (dto) => ({
  company_id: dto?.company_id ?? dto?.id,
  companyCode: dto?.companyCode ?? dto?.code ?? dto?.company_code ?? "",
  name: dto?.name ?? "",
  org_type_id:
    dto?.org_type_id ??
    dto?.organizationTypeId ??
    dto?.organizationType?.id ??
    null,
  created_by: dto?.created_by ?? dto?.createdBy ?? null,
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
  OrganizationType: dto?.OrganizationType ?? dto?.organizationType ?? null,
  creator: dto?.creator ?? null,
});

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

export async function getCompanies() {
  const data = await api.get("/company");
  const arr =
    Array.isArray(data) ? data
    : Array.isArray(data?.data) ? data.data
    : [];
  return arr.map(normalizeCompany);
}

export async function getCompaniesLite() {
  try {
    const data = await api.get("/company");
    const arr =
      Array.isArray(data) ? data
      : Array.isArray(data?.data) ? data.data
      : [];
    return arr.map(normalizeCompany);
  } catch {
    return [];
  }
}

export async function getCompaniesPaginated({
  page = 1,
  limit = 10,
  search = "",
} = {}) {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim())
    query.append("search", String(search).trim());

  const qs = query.toString();
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/company" : (
      "/hierarchical-view/relation/company/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  const data =
    Array.isArray(res?.data) ? res.data
    : Array.isArray(res) ? res
    : [];
  const meta =
    res?.meta && typeof res.meta === "object" ?
      res.meta
    : {
        total: data.length,
        page: Number(page) || 1,
        limit: Number(limit) || 10,
        totalPages: 1,
      };

  return {
    data: data.map(normalizeCompany),
    meta: {
      total: Number(meta.total) || 0,
      page: Number(meta.page) || 1,
      limit: Number(meta.limit) || Number(limit) || 10,
      totalPages: Number(meta.totalPages) || 1,
    },
  };
}

export async function getCompanyNames() {
  const data = await api.get("/company/companyName");
  const arr = Array.isArray(data) ? data : [];
  return arr.map(normalizeCompany);
}

export async function getCompanyDetails(companyId) {
  const cid = Number(companyId);
  if (!Number.isInteger(cid) || cid <= 0) {
    throw new Error("Valid company id is required");
  }
  const payload = await api.get(`/company/${cid}/details`);
  const base =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  return {
    company_id: Number(base?.company_id ?? cid),
    company_name: String(base?.company_name ?? ""),
    functions: Array.isArray(base?.functions) ? base.functions : [],
  };
}

export async function createCompany({
  companyCode,
  name,
  org_type_id,
  created_by,
  seedCLevelFunctions,
}) {
  const payload = {
    companyCode: String(companyCode || "").trim(),
    name: String(name || "").trim(),
  };
  if (org_type_id !== undefined && org_type_id !== null) {
    payload.org_type_id = Number(org_type_id);
  }
  if (seedCLevelFunctions === true) {
    payload.seedCLevelFunctions = true;
  }
  const res = await api.post("/company", payload);
  // If backend returns empty, synthesize using payload
  return res ? normalizeCompany(res) : normalizeCompany(payload);
}

// Note: Including id in body assumes backend allows updating code (id).
// If not, we will revise to only send name and provide a separate code-migration flow.
export async function patchCompany(id, { companyCode, name, org_type_id }) {
  const payload = {};
  if (companyCode !== undefined)
    payload.companyCode = String(companyCode).trim();
  if (name !== undefined) payload.name = String(name).trim();
  if (org_type_id !== undefined && org_type_id !== null)
    payload.org_type_id = Number(org_type_id);
  const res = await api.patch(`/company/${id}`, payload);
  // If backend returns 204/empty, synthesize updated object using provided fields
  return res ?
      normalizeCompany(res)
    : normalizeCompany({
        id,
        companyCode: payload.companyCode,
        name: payload.name,
        org_type_id: payload.org_type_id,
      });
}

export async function deleteCompany(id) {
  return api.delete(`/company/${id}`);
}

export async function deleteCompaniesBulk(ids = []) {
  await Promise.all(ids.map((i) => deleteCompany(i)));
}

// POST /hierarchical-view/relation/company/bulk
// Body: { userIds: number[] }
// Aggregates associatedCompany + createdCompanies for each user, de-duplicated by company_id
export async function getCompaniesByUsersBulk(userIds = []) {
  const payload = {
    userIds: (userIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isInteger(n)),
  };
  if (payload.userIds.length === 0) return [];
  const key = payload.userIds
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (!getCompaniesByUsersBulk._inflight)
    getCompaniesByUsersBulk._inflight = new Map();
  if (getCompaniesByUsersBulk._inflight.has(key)) {
    return await getCompaniesByUsersBulk._inflight.get(key);
  }
  const p = (async () => {
    const res = await api.post(
      "/hierarchical-view/relation/company/bulk",
      payload,
    );
    const results = Array.isArray(res?.results) ? res.results : [];
    const set = new Map();
    for (const item of results) {
      if (!item || item.success !== true) continue;
      const data = item.data || {};
      const assoc = data.associatedCompany;
      if (assoc) {
        const norm = normalizeCompany(assoc);
        if (norm.company_id != null) set.set(Number(norm.company_id), norm);
      }
      const created =
        Array.isArray(data.createdCompanies) ? data.createdCompanies : [];
      for (const c of created) {
        const norm = normalizeCompany(c);
        if (norm.company_id != null) set.set(Number(norm.company_id), norm);
      }
    }
    return Array.from(set.values());
  })();
  getCompaniesByUsersBulk._inflight.set(key, p);
  try {
    return await p;
  } finally {
    getCompaniesByUsersBulk._inflight.delete(key);
  }
}
